import React, {useEffect, useState} from 'react'
import {Helmet} from 'react-helmet-async'
import {Link} from 'react-router-dom'
import {BetaFlagMarker} from '../../../components/feature-flags'
import {Button} from '../../../components/library/buttons'
import {
  DropdownBody,
  DropdownFooter,
  DropdownItem
} from '../../../components/library/dropdown/content'
import {TextInput} from '../../../components/library/form'
import {Icon} from '../../../components/library/icon/icon'
import {
  List,
  NoResultsPermissions,
  useListOrderBy
} from '../../../components/library/list'
import {Tag} from '../../../components/library/tag/tag'
import {FormModal} from '../../../components/modals'
import {OverflowFooter} from '../../../components/overflow'
import {
  TagDetailFragment,
  useCreateTagMutation,
  useGetTagsLazyQuery,
  useUpdateTagMutation
} from '../../../data/workbench-api/generated/tags'
import {
  PermissionAction,
  PermissionResource,
  TagsOrderBy
} from '../../../data/workbench-api/generated/types'
import {EnvironmentNode} from '../../../environment'
import {useStateChanged} from '../../../hooks/use-state-changed'
import {useAnalytics} from '../../../lib/analytics/analytics'
import {displayDate} from '../../../lib/helpers'
import {tagRegex} from '../../customers/tag-modal'
import {IconHeading, Section, SectionContent, SectionInfo} from '../components'

export const Tags = ({environment}: {environment: EnvironmentNode}) => {
  const environmentId = environment.id
  const analytics = useAnalytics()
  const canView = environment.hasPermission(
    PermissionResource.Tag,
    PermissionAction.View
  )
  const canEdit = environment.hasPermission(
    PermissionResource.Tag,
    PermissionAction.Edit
  )
  const [orderBy, setOrderBy] = useListOrderBy<TagsOrderBy>([
    TagsOrderBy.NameAsc
  ])
  const [query, {data, loading, refetch = () => null}] = useGetTagsLazyQuery({
    variables: {environmentId: environment.id, orderBy}
  })
  useEffect(() => {
    if (canView) {
      query()
    }
  }, [canView, query])
  const [updateTag] = useUpdateTagMutation({
    onCompleted: () => {
      analytics.track({
        event: 'Edited configuration',
        properties: {
          org_id: environment.organisationId,
          environment_id: environment.id,
          category: 'configuration>customer-profiles>tags'
        }
      })
    }
  })
  const [createTag] = useCreateTagMutation({
    onCompleted: () => {
      analytics.track({
        event: 'Added configuration',
        properties: {
          org_id: environment.organisationId,
          environment_id: environment.id,
          category: 'configuration>customer-profiles>tags'
        }
      })
    }
  })
  const [tagModal, setTagModal] = useState<Partial<TagDetailFragment>>()

  const tags = data?.tags?.nodes ?? []

  useEffect(() => {
    analytics.track({
      event: 'Viewed tags configuration',
      properties: {
        org_id: environment.organisationId,
        environment_id: environment.id,
        category: 'configuration>customer-profiles>tags'
      }
    })
  }, [analytics, environment])

  return (
    <Section>
      <Helmet>
        <title>Tags</title>
      </Helmet>
      <header>
        <IconHeading>
          <Icon name="label" />
          <a href="#tags">Tags</a>
          <BetaFlagMarker />
        </IconHeading>

        <nav>
          <Button
            disabled={!canEdit}
            onClick={() => setTagModal({name: '', environmentId})}
          >
            New tag
          </Button>
        </nav>
      </header>

      <SectionInfo>
        <p>Edit tags which are available when filtering customers.</p>
      </SectionInfo>

      <SectionContent>
        <List<TagDetailFragment, TagsOrderBy>
          bleed="#f2f2f2"
          loading={loading}
          setOrderBy={setOrderBy}
          orderBy={orderBy}
          columns={[
            {
              id: 'tag',
              header: 'Tag',
              accessor: f => f.name,
              sortKey: TagsOrderBy.NameAsc
            },
            {
              id: 'created',
              header: 'Created',
              accessor: f => displayDate(f.created),
              sortKey: TagsOrderBy.CreatedAsc
            },
            {
              id: 'enabled',
              header: 'Status',
              accessor: f => (
                <>
                  {f.enabled ? (
                    <Tag appearance="green">Enabled</Tag>
                  ) : (
                    <Tag appearance="light">Disabled</Tag>
                  )}
                </>
              )
            }
          ]}
          rows={tags}
          renderOverflow={(tag, closeDropdown) => (
            <Overflow
              tag={tag}
              onToggleTag={inputTag =>
                updateTag({
                  variables: {
                    id: inputTag.id,
                    name: inputTag.name,
                    enabled: !tag.enabled,
                    environmentId
                  }
                })
              }
              onEditTag={t => setTagModal(t)}
              closeDropdown={closeDropdown}
              canEdit={canEdit}
            />
          )}
          renderEmpty={canView ? undefined : () => <NoResultsPermissions />}
        />
      </SectionContent>
      {tagModal && (
        <TagModal
          handleClose={() => setTagModal(undefined)}
          tag={tagModal}
          onSave={async tag => {
            const updating = !!tag.id
            if (updating) {
              await updateTag({
                variables: {
                  id: tag.id,
                  name: tag.name,
                  environmentId,
                  enabled: !!tag.enabled
                }
              })
            } else {
              await createTag({
                variables: {environmentId, name: tag.name}
              })
              await refetch()
            }
          }}
        />
      )}
    </Section>
  )
}

const Overflow = ({
  tag,
  onEditTag,
  onToggleTag,
  closeDropdown,
  canEdit
}: {
  tag: TagDetailFragment
  onEditTag: (t: TagDetailFragment) => void
  onToggleTag: (t: TagDetailFragment) => void
  closeDropdown: () => void
  canEdit: boolean
}) => {
  return (
    <DropdownBody>
      <DropdownItem disabled={!canEdit}>
        <a
          onClick={() => {
            closeDropdown()
            onEditTag(tag)
          }}
        >
          Edit tag
        </a>
      </DropdownItem>
      {tag.enabled && (
        <DropdownItem>
          <Link
            to={`../../customers?filters=${encodeURIComponent(
              `atomic___tag,EQUALS,${tag.id}`
            )}`}
          >
            View tagged customers
          </Link>
        </DropdownItem>
      )}
      <DropdownItem disabled={!canEdit}>
        <a
          onClick={() => {
            closeDropdown()
            onToggleTag(tag)
          }}
        >
          {tag.enabled ? 'Disable' : 'Enable'}
        </a>
      </DropdownItem>
      <DropdownFooter>
        <OverflowFooter
          id={tag.id}
          updated={tag.updated}
          created={tag.created}
        />
      </DropdownFooter>
    </DropdownBody>
  )
}

const TagModal = ({
  tag,
  onSave,
  handleClose
}: {
  tag: Partial<TagDetailFragment>
  onSave: (t: TagDetailFragment) => void
  handleClose: () => void
}) => {
  const [local, setLocal, dirty] =
    useStateChanged<Partial<TagDetailFragment>>(tag)
  const [loading, setLoading] = useState(false)
  const wrappedHandleClose = () => {
    if (dirty) {
      if (confirm('You have unsaved changes are you sure?')) {
        handleClose()
      }
    } else {
      handleClose()
    }
  }
  return (
    <FormModal
      title={`Tag ${local.name ? `"${local.name}"` : ''}`}
      handleClose={wrappedHandleClose}
      onSubmit={() => {
        setLoading(true)
        onSave(local as TagDetailFragment)
        setLoading(false)
        handleClose()
      }}
      isValid={!loading && !!local.name}
    >
      <TextInput
        label="Tag name"
        value={local.name || ''}
        onChange={name =>
          setLocal(t => ({
            ...t,
            name: name.replace(tagRegex, '')
          }))
        }
      />
    </FormModal>
  )
}
